import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Fire, UserPlus, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `/api`;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const lastNotifIdsRef = useRef(new Set());

  useEffect(() => {
    fetchNotifications(true);
    const interval = setInterval(() => fetchNotifications(false), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async (isFirstLoad) => {
    try {
      const { data } = await axios.get(`${API}/notifications`, {
        withCredentials: true,
      });
      const notifs = data.notifications || [];
      
      // Show toast for new notifications (skip on first load)
      if (!isFirstLoad) {
        notifs.forEach((notif) => {
          const notifKey = `${notif.type}_${notif.timestamp}`;
          if (!lastNotifIdsRef.current.has(notifKey) && !notif.read) {
            const icon = notif.type === 'hot_lead' ? '🔥' : notif.type === 'new_lead' ? '✨' : '📌';
            toast(`${icon} ${notif.title}`, {
              description: notif.message,
              duration: 5000,
            });
          }
        });
      }
      
      // Update tracking set
      lastNotifIdsRef.current = new Set(notifs.map((n) => `${n.type}_${n.timestamp}`));
      
      setNotifications(notifs);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      fetchNotifications(true);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'hot_lead':
        return <Fire size={18} weight="fill" className="text-red-500" />;
      case 'new_lead':
        return <UserPlus size={18} weight="fill" className="text-blue-500" />;
      case 'new_assignment':
        return <ArrowRight size={18} weight="fill" className="text-emerald-500" />;
      default:
        return <Bell size={18} weight="fill" className="text-slate-500" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `há ${diffMin}min`;
    if (diffHr < 24) return `há ${diffHr}h`;
    return `há ${diffDay}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center transition-all duration-200"
        data-testid="notification-bell-button"
      >
        <Bell size={20} weight="duotone" className="text-slate-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-[500px] flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Notificações</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                data-testid="mark-all-read-button"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Nenhuma notificação ainda</p>
              </div>
            ) : (
              notifications.map((notif, index) => (
                <div
                  key={`${notif.type}_${notif.timestamp}_${index}`}
                  className={`p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    !notif.read ? 'bg-blue-50/30' : ''
                  }`}
                  data-testid={`notification-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{getNotifIcon(notif.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{notif.title}</p>
                      <p className="text-xs text-slate-600 mt-1">{notif.message}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(notif.timestamp)}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
